import {
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import Header from '../../../components/Header';
import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { useTheme } from '@emotion/react';
import { tokens } from '../../../theme';

const PackageDuplicate = () => {
  const { id } = useParams();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const [openToast, setOpenToast] = useState(false);
  const [toastMessage, setToastmessage] = useState('');
  const [loading, setLoading] = useState(false);

  const [packageDetails, setPackageDetails] = useState({
    PackageName: '',
    PackageDescription: '',
    PackageImage: '',
    CountryID: '',
    ExcursionIDs: [],
    PackagePrice: '',
  });

  const [existingImageURL, setExistingImageURL] = useState('');
  const [newImage, setNewImage] = useState(null);
  const [countries, setCountries] = useState([]);
  const [excursions, setExcursions] = useState([]);

  const token = localStorage.getItem('token');

  const showToast = (msg) => {
    setToastmessage(msg);
    setOpenToast(true);
  };

  const getCountryNames = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_APILINK}/countrynames`, { headers: { token } });
      const data = await res.json();
      setCountries(data);
    } catch (e) {
      showToast('Failed to load countries');
    }
  };

  const getExcursions = async (countryID) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_APILINK}/countryExcursions/${countryID}`, { headers: { token } });
      const data = await res.json();
      setExcursions(data.excursions || []);
    } catch (e) {
      showToast('Failed to load excursions');
    }
  };

  const getPackageDetails = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_APILINK}/packages/${id}`, { headers: { token } });
      const data = await res.json();
      if (data.success && data.package) {
        const pkg = data.package;
        setPackageDetails({
          PackageName: pkg.PackageName,
          PackageDescription: pkg.PackageDescription,
          PackageImage: pkg.PackageImage,
          CountryID: pkg.CountryID,
          ExcursionIDs: pkg.ExcursionIDs || [],
          PackagePrice: pkg.PackagePrice,
        });
        setExistingImageURL(pkg.PackageImageURL || '');
      } else {
        navigate('/package');
      }
    } catch (e) {
      console.error(e);
      navigate('/package');
    }
  };

  useEffect(() => {
    getCountryNames();
    getPackageDetails();
  }, []);

  useEffect(() => {
    if (packageDetails.CountryID) {
      getExcursions(packageDetails.CountryID);
    } else {
      setExcursions([]);
    }
  }, [packageDetails.CountryID]);

  const handleChange = (e) => {
    setPackageDetails((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleCountryChange = (e) => {
    setPackageDetails((prev) => ({
      ...prev,
      CountryID: e.target.value,
      ExcursionIDs: [],
    }));
  };

  const handleExcursionToggle = (excursionId) => {
    setPackageDetails((prev) => {
      const exists = prev.ExcursionIDs.includes(excursionId);
      return {
        ...prev,
        ExcursionIDs: exists
          ? prev.ExcursionIDs.filter((eid) => eid !== excursionId)
          : [...prev.ExcursionIDs, excursionId],
      };
    });
  };

  const handleSelectAll = () => {
    const allIds = excursions.map((e) => e.ExcursionID);
    const allSelected = allIds.every((eid) => packageDetails.ExcursionIDs.includes(eid));
    setPackageDetails((prev) => ({
      ...prev,
      ExcursionIDs: allSelected ? [] : allIds,
    }));
  };

  const imageHandler = (e) => {
    setNewImage(e.target.files[0]);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (
      !packageDetails.PackageName ||
      !packageDetails.PackageDescription ||
      !packageDetails.CountryID ||
      !packageDetails.PackagePrice
    ) {
      showToast('Please fill all details');
      setLoading(false);
      return;
    }

    if (!packageDetails.PackageImage && !newImage) {
      showToast('Please select a package image');
      setLoading(false);
      return;
    }

    let imageName = packageDetails.PackageImage;

    if (newImage) {
      const formData = new FormData();
      formData.append('file', newImage);
      try {
        const imgRes = await fetch(`${process.env.REACT_APP_APILINK}/packages/uploadimage`, {
          headers: { token },
          method: 'POST',
          body: formData,
        });
        const imageData = await imgRes.json();
        if (imageData.success) {
          imageName = imageData.image;
        } else {
          showToast(imageData.message || 'Image upload failed');
          setLoading(false);
          return;
        }
      } catch (error) {
        showToast('Image upload failed');
        setLoading(false);
        return;
      }
    }

    try {
      const res = await fetch(`${process.env.REACT_APP_APILINK}/packages`, {
        method: 'POST',
        headers: { 'Content-type': 'application/json', token },
        body: JSON.stringify({ ...packageDetails, PackageImage: imageName }),
      });
      const data = await res.json();
      if (data.message) {
        showToast(data.message);
        if (data.success) {
          setTimeout(() => navigate('/package'), 2000);
        }
      }
    } catch (error) {
      showToast('Error creating package');
    }

    setLoading(false);
  };

  const handleCloseToast = (event, reason) => {
    if (reason === 'clickaway') return;
    setOpenToast(false);
  };

  const currentImageSrc = newImage
    ? URL.createObjectURL(newImage)
    : existingImageURL || null;

  const allSelected =
    excursions.length > 0 &&
    excursions.every((e) => packageDetails.ExcursionIDs.includes(e.ExcursionID));

  return (
    <Box p="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center" rowGap="10px" flexWrap="wrap-reverse">
        <Header title="DUPLICATE PACKAGE" subtitle="Duplicate an existing package" />
        <Button
          onClick={() => navigate(-1)}
          sx={{
            backgroundColor: colors.blueAccent[700],
            color: colors.grey[100],
            fontSize: '14px',
            fontWeight: 'bold',
            padding: '10px 30px',
            '&:hover': { backgroundColor: colors.blueAccent[800] },
          }}
        >
          Go Back
        </Button>
      </Box>

      <form onSubmit={handleFormSubmit}>
        <Box display="grid" gap="1rem" pb="20px">

          <TextField
            fullWidth
            variant="filled"
            type="text"
            label="Package Name"
            value={packageDetails.PackageName}
            onChange={handleChange}
            name="PackageName"
            required
          />

          <TextField
            fullWidth
            variant="filled"
            type="text"
            label="Package Description"
            value={packageDetails.PackageDescription}
            onChange={handleChange}
            name="PackageDescription"
            required
          />

          <TextField
            select
            variant="filled"
            label="Country"
            value={packageDetails.CountryID}
            onChange={handleCountryChange}
            name="CountryID"
            required
          >
            {countries.map((c) => (
              <MenuItem key={c.CountryID} value={c.CountryID}>
                {c.CountryName}
              </MenuItem>
            ))}
          </TextField>

          {packageDetails.CountryID && (
            <Box>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="h6" fontWeight={600} color={colors.grey[100]}>
                  Excursions
                  {packageDetails.ExcursionIDs.length > 0 && (
                    <Chip
                      label={`${packageDetails.ExcursionIDs.length} selected`}
                      size="small"
                      sx={{ ml: 1.5, backgroundColor: colors.greenAccent[700], color: colors.grey[100], fontWeight: 600 }}
                    />
                  )}
                </Typography>
                {excursions.length > 0 && (
                  <Button
                    size="small"
                    onClick={handleSelectAll}
                    sx={{ color: colors.blueAccent[300], textTransform: 'none', fontWeight: 600, fontSize: '14px' }}
                  >
                    {allSelected ? 'Deselect All' : 'Select All'}
                  </Button>
                )}
              </Box>

              {excursions.length === 0 ? (
                <Typography variant="body2" color={colors.grey[400]} sx={{ fontStyle: 'italic', py: 1 }}>
                  No excursions available for this country.
                </Typography>
              ) : (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
                    gap: '8px',
                    maxHeight: '320px',
                    overflowY: 'auto',
                    pr: 0.5,
                    '&::-webkit-scrollbar': { width: '6px' },
                    '&::-webkit-scrollbar-track': { background: colors.primary[500] },
                    '&::-webkit-scrollbar-thumb': { background: colors.grey[600], borderRadius: '3px' },
                  }}
                >
                  {excursions.map((excursion) => {
                    const isChecked = packageDetails.ExcursionIDs.includes(excursion.ExcursionID);
                    return (
                      <Box
                        key={excursion.ExcursionID}
                        onClick={() => handleExcursionToggle(excursion.ExcursionID)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          px: 1.5,
                          py: 1,
                          borderRadius: '8px',
                          cursor: 'pointer',
                          border: `1.5px solid ${isChecked ? colors.greenAccent[500] : colors.grey[700]}`,
                          backgroundColor: isChecked ? `${colors.greenAccent[900]}44` : colors.primary[400],
                          transition: 'all 0.15s ease',
                          '&:hover': {
                            borderColor: colors.greenAccent[500],
                            backgroundColor: `${colors.greenAccent[900]}22`,
                          },
                        }}
                      >
                        {isChecked
                          ? <CheckBoxIcon sx={{ color: colors.greenAccent[400], fontSize: 20, flexShrink: 0 }} />
                          : <CheckBoxOutlineBlankIcon sx={{ color: colors.grey[500], fontSize: 20, flexShrink: 0 }} />
                        }
                        <Typography
                          variant="body2"
                          sx={{
                            color: isChecked ? colors.greenAccent[300] : colors.grey[100],
                            fontWeight: isChecked ? 600 : 400,
                            lineHeight: 1.3,
                          }}
                        >
                          {excursion.ExcursionName}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          )}

          <TextField
            fullWidth
            variant="filled"
            type="number"
            label="Package Price"
            value={packageDetails.PackagePrice}
            onChange={handleChange}
            name="PackagePrice"
            required
            inputProps={{ min: 0, step: '0.01' }}
          />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <Typography variant="h5" mt="10px" color={colors.grey[100]}>
              Package Image
            </Typography>
            <label
              htmlFor="file-input"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                padding: '15px',
                background: colors.primary[400],
                fontSize: '18px',
                fontWeight: 'bold',
                cursor: 'pointer',
                borderRadius: '10px',
                width: '400px',
                aspectRatio: '16/9',
              }}
            >
              {currentImageSrc ? (
                <img
                  src={currentImageSrc}
                  alt="Package"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px' }}
                />
              ) : (
                <>
                  <CloudUploadIcon fontSize="large" /> Upload Image
                </>
              )}
            </label>
            <input type="file" accept="image/*" onChange={imageHandler} name="image" id="file-input" hidden />
            {currentImageSrc && (
              <Typography variant="caption" sx={{ color: colors.grey[400], mt: '4px', display: 'block' }}>
                Click the image above to replace it
              </Typography>
            )}
          </Box>

          <Button
            type="submit"
            disabled={loading}
            sx={{
              backgroundColor: colors.blueAccent[700],
              color: colors.grey[100],
              width: 'fit-content',
              fontSize: '14px',
              fontWeight: 'bold',
              padding: '10px 30px',
              '&:hover': { backgroundColor: colors.blueAccent[800] },
            }}
          >
            {loading ? 'Duplicating...' : 'Duplicate'}
          </Button>
        </Box>
      </form>

      <Snackbar
        open={openToast}
        autoHideDuration={5000}
        onClose={handleCloseToast}
        message={toastMessage}
        action={
          <IconButton size="small" color="inherit" onClick={handleCloseToast}>
            <CloseIcon fontSize="small" />
          </IconButton>
        }
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </Box>
  );
};

export default PackageDuplicate;
